import { Space } from "antd";
import { useEffect, useState } from "react";
import {
  Button,
  Form,
  OverlayTrigger,
  Tooltip as RBTooltip,
  Badge,
} from "react-bootstrap";
import { toast } from "sonner";
import {
  createJustificatif,
  updateAbsence,
  updateJustificatif,
} from "../../../api/etudiants";
import {
  createFileAttachement,
  deleteFileAttachementByID,
} from "../../../api/fileAttachement";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { usePermissions } from "../../../core/common/context/PermissionsUserContext";
import { useSelector } from "react-redux";
import { openSupportInNewTab } from "../../../helper/supportHelper";

const ReportAbsenceBubble = ({
  id_student,
  absence,
  open,
  dayIndex,
  sessionIndex,
  allAbsences,
  allSessions,
  onSave,
  onCancel,
}: {
  id_student: string;
  absence: any;
  open: any;
  dayIndex: number;
  sessionIndex: number;
  allAbsences: any[];
  allSessions: any[];
  onSave: () => void;
  onCancel: () => void;
}) => {
  // permissions de l'utilisateur connecté
  const { permissions } = usePermissions();
  const isSuperAdmin = useSelector(
    (state: any) => state.auth.user.isSuperAdmin,
  );

  const [absenceCopy, setAbsenceCopy] = useState(absence);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filesLoading, setFilesLoading] = useState<any[]>([]);

  // condition pour vérifier si droit d'édition ou pas
  const canEdit = (): boolean => {
    return (
      [buildPermission(Resource.STUDENT_ABSENCE, Action.UPDATE)].some(
        (requiredPerm: any) => permissions.includes(requiredPerm),
      ) || isSuperAdmin
    );
  };

  // Helper: compose all selected absence IDs (current + linked)
  const composeSelectedAbsenceIds = () => {
    const baseId = absenceCopy?.abs?.id_absence
      ? [absenceCopy.abs.id_absence]
      : [];
    const linked = absenceCopy?.linkedAbsenceIds || [];
    // Deduplicate while preserving order
    const set = new Set<number>([...baseId, ...linked]);
    return Array.from(set);
  };

  // Helper component: only show tooltip when text is actually truncated
  const TruncateWithTooltip = ({ text }: { text: string }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [hover, setHover] = useState(false);
    const onRef = (el: HTMLSpanElement | null) => {
      if (!el) return;
      const isOverflowing = el.scrollWidth > el.clientWidth;
      setShowTooltip(isOverflowing);
    };
    const content = (
      <span
        ref={onRef}
        className="text-truncate"
        style={{ display: "inline-block", maxWidth: "100%" }}
        aria-label={text}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {text}
      </span>
    );
    return showTooltip ? (
      <OverlayTrigger
        placement="top"
        overlay={
          <RBTooltip id={`tt-${Math.random().toString(36).slice(2)}`}>
            {text}
          </RBTooltip>
        }
        // Ensure accessibility: tooltips appear on focus too
        trigger={["hover", "focus"]}
      >
        {content as any}
      </OverlayTrigger>
    ) : (
      content
    );
  };

  // Compute unjustified sessions once for performance
  const unjustifiedSessions = (() => {
    const sessions = allAbsences
      .map((absence) => absence.sessions)
      .flat()
      .filter(
        (session) =>
          // !session?.abs?.justified &&
          session?.abs?.id_absence !== absenceCopy?.abs?.id_absence,
      );
    return sessions;
  })();

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (absenceCopy?.abs?.justified) {
        const reason = absenceCopy?.abs?.justificatif?.reason || "";
        if (!reason.trim()) {
          setErrorMsg("Veuillez entrer le justificatif du retard");
          return;
        }
        let newJustificatif = null;
        const ids = composeSelectedAbsenceIds();

        // si il existe déjà un justificatif lié à cette absence, on le met à jour
        if (absenceCopy?.abs?.justificatif?.id) {
          await updateJustificatif(
            absenceCopy.abs.justificatif?.id,
            {
              reason,
              id_absenceStudents: ids,
            },
            buildPermissionContext(Resource.STUDENT_ABSENCE, Action.UPDATE),
          );

          // enregistrer les nouveaux fichiers joins avec toutes les absences sélectionnés
          if (newFiles.length > 0) {
            const formData = new FormData();
            formData.append("entity_type", "JustificationAbsenceStudent");
            formData.append("entity_id", absenceCopy.abs.justificatif?.id);
            newFiles.forEach((file: any) => {
              formData.append("files", file);
            });
            createFileAttachement(
              formData,
              buildPermissionContext(Resource.STUDENT_ABSENCE, Action.UPDATE),
            );
          }
          onSave();
          toast.success("Justificatif mis à jour avec succès");
        }
        // sinon, on crée un nouveau justificatif
        else {
          newJustificatif = await createJustificatif(
            {
              reason,
              id_absenceStudents: ids,
            },
            buildPermissionContext(Resource.STUDENT_ABSENCE, Action.UPDATE),
          );
          // console.log("newJustificatif: ", newJustificatif);
          // enregistrer les nouveaux fichiers joins avec toutes les absences sélectionnés
          if (newFiles.length > 0) {
            const formData = new FormData();
            formData.append("entity_type", "JustificationAbsenceStudent");
            formData.append("entity_id", newJustificatif?.data?.id);
            newFiles.forEach((file: any) => {
              formData.append("files", file);
            });
            createFileAttachement(
              formData,
              buildPermissionContext(Resource.STUDENT_ABSENCE, Action.UPDATE),
            );
          }
          toast.success("Justificatif créé avec succès");
        }
      }
      // si l'absence n'est plus justifiée, on supprime le justificatif lié à cette absence
      else if (
        !absenceCopy?.abs?.justified &&
        absenceCopy?.abs?.justificatif?.id
      ) {
        // mettre à jour l'absence pour indiquer qu'elle n'est plus justifiée
        await updateAbsence(absenceCopy.abs.id_absence, {
          justified: false,
          //id_sessions: [absenceCopy.id],
          id_sessions: absenceCopy.abs.id_session,
          id_justificatif: absenceCopy.abs.justificatif?.id,
          id_courseVersionEDT: absenceCopy?.abs?.courseVersionEDT?.id,
          id_studentProfil: id_student,
        });
        toast.success("Status de l'absence mis à jour avec succès");
      }
      onSave();
      onCancel();
    } catch (error: any) {
      // console.log("erreur de modification de l'absence : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open[`${dayIndex}-${sessionIndex}`]) setAbsenceCopy(null);
    setAbsenceCopy(absence);
  }, [open, absence]);

  // Derive existing and new files in state for visual distinction and deduplication
  const existingFiles = (
    absenceCopy?.abs?.justificatif?.existingFiles || []
  ).map((f: any) => ({
    id: f?.id,
    name: f?.name || f?.filename || "Fichier",
    url: f?.url || f?.path || undefined,
  }));
  const newFiles = absenceCopy?.abs?.justificatif?.newFiles || [];

  // Initialize existingFiles from justificatif.files if present and not File objects
  useEffect(() => {
    const j = absenceCopy?.abs?.justificatif;
    if (!j) return;
    const files = (j as any).files;
    if (
      Array.isArray(files) &&
      files.length > 0 &&
      !(files[0] instanceof File) &&
      !j.existingFiles
    ) {
      setAbsenceCopy({
        ...absenceCopy,
        abs: {
          ...absenceCopy.abs,
          justificatif: {
            ...j,
            existingFiles: files,
          },
        },
      });
    }
  }, [absenceCopy?.abs?.justificatif?.files]);

  const handleAddFiles = (filesList: FileList | null) => {
    if (!filesList) return;
    const incoming = Array.from(filesList);
    const existingNames = new Set(existingFiles.map((f: any) => f.name));
    const currentNew = newFiles;
    const currentNewKey = new Set(
      currentNew.map((f: File) => `${f.name}-${f.size}`),
    );
    const filtered = incoming.filter((f) => {
      const key = `${f.name}-${f.size}`;
      if (existingNames.has(f.name)) return false; // avoid re-inserting files already linked
      if (currentNewKey.has(key)) return false; // avoid duplicates in new files
      return true;
    });
    if (filtered.length === 0) return;
    setAbsenceCopy({
      ...absenceCopy,
      abs: {
        ...absenceCopy.abs,
        justificatif: {
          ...absenceCopy.abs.justificatif,
          newFiles: [...currentNew, ...filtered],
        },
      },
    });
  };

  const handleGetFile = async (fileId: string, fileName: string) => {
    try {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "open" }));

      await openSupportInNewTab({
        entityType: "JustificationAbsenceStudent",
        fileName,
        context: buildPermissionContext(Resource.STUDENT_ABSENCE, Action.READ),
      });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "" }));
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFileAttachementByID(
        fileId,
        buildPermissionContext(Resource.STUDENT_ABSENCE, Action.UPDATE),
      );
      // setAbsenceCopy({
      //   ...absenceCopy,
      //   abs: {
      //     ...absenceCopy.abs,
      //     justificatif: {
      //       ...absenceCopy.abs.justificatif,
      //       existingFiles: existingFiles.filter((f: any) => f.id !== fileId),
      //     },
      //   },
      // });
      onSave();
      toast.success("Fichier justificatif supprimer avec succès");
    } catch (error: any) {
      // console.log(
      //   "erreur lors de la supprision dun fichier justificatif : ",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev) => ({ ...prev, [fileId]: "" }));
    }
  };

  return (
    <div>
      <p>
        <strong> Cours</strong> :{" "}
        {absenceCopy?.abs?.courseVersionEDT?.CourseClass?.Course?.Subject
          ?.label || ""}
      </p>
      <Form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!e.currentTarget.checkValidity()) {
            e.currentTarget.reportValidity();
            return;
          }
          if (
            absenceCopy?.abs?.justified &&
            !absenceCopy?.abs?.justificatif?.reason
          ) {
            setErrorMsg("Veuillez entrer le justificatif du retard");
            return;
          }
          handleSave();
        }}
      >
        <Form.Group className="mb-3" controlId="formJustified">
          <Form.Check
            type="switch"
            label="Justifiée"
            checked={absenceCopy?.abs?.justified || false}
            disabled={!canEdit()}
            onChange={(e) =>
              setAbsenceCopy({
                ...absenceCopy,
                abs: {
                  ...absenceCopy.abs,
                  justified: e.target.checked,
                },
              })
            }
          />
          {absenceCopy?.abs?.justified ? (
            <>
              <Form.Group>
                <Form.Label>
                  Justificatif de l'absence
                  <Form.Text className="text-danger"> *</Form.Text>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={absenceCopy?.abs?.justificatif?.reason || ""}
                  style={{ width: "100%" }}
                  disabled={!canEdit()}
                  onChange={(e) =>
                    setAbsenceCopy({
                      ...absenceCopy,
                      abs: {
                        ...absenceCopy.abs,
                        justificatif: {
                          ...absenceCopy.abs.justificatif,
                          reason: e.target.value,
                        },
                      },
                    })
                  }
                />
                {errorMsg && <small className="text-danger">{errorMsg}</small>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Pièces jointes</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  disabled={!canEdit()}
                  onChange={(e) =>
                    handleAddFiles((e.target as HTMLInputElement).files)
                  }
                />
                {/* Existing files */}
                {existingFiles.length > 0 && (
                  <div className="mt-2" aria-live="polite">
                    {existingFiles.map((file: any, idx: number) => (
                      <div
                        key={`ex-${file.id ?? idx}`}
                        className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border rounded p-2 mb-1 gap-2"
                      >
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <Badge bg="secondary" className="me-2">
                            Existant
                          </Badge>
                          <TruncateWithTooltip text={file.name} />
                        </div>
                        {file.name ? (
                          <div className="d-flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!canEdit()}
                              onClick={() => {
                                setFilesLoading((prev) => ({
                                  ...prev,
                                  [file.id]: "open",
                                }));
                                handleGetFile(file.id, file.name);
                              }}
                            >
                              {filesLoading[file.id] === "open"
                                ? "En cours..."
                                : "Ouvrir"}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={!canEdit()}
                              onClick={() => {
                                setFilesLoading((prev) => ({
                                  ...prev,
                                  [file.id]: "delete",
                                }));
                                handleDeleteFile(file.id);
                              }}
                            >
                              {filesLoading[file.id] === "delete"
                                ? "Supression..."
                                : "Supprimer"}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted" aria-hidden>
                            —
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* New files */}
                <div className="mt-2" aria-live="polite">
                  {newFiles.map((file: File, idx: number) => (
                    <div
                      key={`new-${idx}`}
                      className="d-flex justify-content-between align-items-center border rounded p-2 mb-1"
                    >
                      <div style={{ maxWidth: "80%" }}>
                        <Badge bg="warning" text="dark" className="me-2">
                          Nouveau
                        </Badge>
                        <TruncateWithTooltip text={file.name} />
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={!canEdit()}
                        onClick={() => {
                          const updatedFiles = newFiles.filter(
                            (_: File, i: number) => i !== idx,
                          );
                          setAbsenceCopy({
                            ...absenceCopy,
                            abs: {
                              ...absenceCopy.abs,
                              justificatif: {
                                ...absenceCopy.abs.justificatif,
                                newFiles: updatedFiles,
                              },
                            },
                          });
                        }}
                      >
                        Retirer
                      </Button>
                    </div>
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Absences non justifiées à associer</Form.Label>
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {unjustifiedSessions
                    .sort((a: any, b: any) => {
                      // Sort by date descending
                      const dateA = new Date(
                        a.abs.courseVersionEDT?.date_course || 0,
                      );
                      const dateB = new Date(
                        b.abs.courseVersionEDT?.date_course || 0,
                      );
                      if (dateB.getTime() !== dateA.getTime()) {
                        return dateB.getTime() - dateA.getTime();
                      }
                      // Then by session start_time descending
                      const sessionA = allSessions.find(
                        (s: any) => s.id === a.id,
                      );
                      const sessionB = allSessions.find(
                        (s: any) => s.id === b.id,
                      );
                      const timeA = sessionA?.start_time || "";
                      const timeB = sessionB?.start_time || "";
                      return timeB.localeCompare(timeA);
                    })
                    .map((a: any) => {
                      const sessionMeta = allSessions.find(
                        (s: any) => s.id === a.id,
                      );
                      const dateStr = new Date(
                        a.abs.courseVersionEDT?.date_course || "",
                      ).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        weekday: "short",
                      });
                      const courseLabel =
                        a.abs.courseVersionEDT?.CourseClass?.Class?.label ||
                        "Cours";
                      const timeSlot = sessionMeta
                        ? `${sessionMeta?.name || "Séance"} (${
                            sessionMeta?.start_time?.slice(0, 5) || ""
                          } - ${sessionMeta?.end_time?.slice(0, 5) || ""})`
                        : a.abs.courseVersionEDT?.startTime || "Séance";
                      const label = `${dateStr} • ${timeSlot} • ${courseLabel}`;
                      const isChecked =
                        absenceCopy?.abs?.justificatif?.linkedAbsenceIds?.includes(
                          a.abs.id_absence,
                        ) || false;

                      return (
                        <div
                          key={`${a.abs.id_absence}-${a.id}`}
                          className="mb-2"
                        >
                          <Form.Check
                            type="checkbox"
                            checked={isChecked}
                            // désactiver si cette absence n'as aucun lien avec l'abscence principale de ce pop-up mais est liées à d'autres
                            disabled={
                              (a.abs.justificatif?.linkedAbsenceIds?.length >
                                0 &&
                                !a.abs.justificatif.linkedAbsenceIds.includes(
                                  absenceCopy?.abs?.id_absence,
                                )) ||
                              !canEdit()
                            }
                            onChange={(e) => {
                              const linked =
                                absenceCopy?.abs?.justificatif
                                  ?.linkedAbsenceIds || [];
                              const updated = e.target.checked
                                ? [...linked, a.abs.id_absence]
                                : linked.filter(
                                    (id: number) => id !== a.abs.id_absence,
                                  );
                              setAbsenceCopy((prev: any) => ({
                                ...prev,
                                abs: {
                                  ...prev.abs,
                                  justificatif: {
                                    ...prev.abs.justificatif,
                                    linkedAbsenceIds: updated,
                                  },
                                },
                              }));
                            }}
                            id={`abs-${a.abs.id_absence}`}
                            label={<TruncateWithTooltip text={label} />}
                            aria-describedby={`tt-${a.abs.id_absence}`}
                          />
                        </div>
                      );
                    })}
                </div>
              </Form.Group>
            </>
          ) : null}
        </Form.Group>
        <Space>
          <Button variant="danger" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !canEdit()}
          >
            {isLoading ? "Enregistremenrt..." : "Enregistrer"}
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default ReportAbsenceBubble;
